import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { HERO_TAGS } from "@/data/content";
import { MaskLine, EASE } from "./Reveal";
import { Ledger } from "./Ledger";
import { scrollToId } from "@/lib/lenis";

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const bigY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section ref={ref} id="top" className="relative bg-[#16302B] text-white overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 hairline-grid pointer-events-none" />
      <motion.div
        style={{ y: bigY }}
        className="absolute -right-10 bottom-[-6rem] font-display italic text-[22rem] sm:text-[30rem] leading-none text-white/[0.03] select-none pointer-events-none"
        aria-hidden="true"
      >
        S
      </motion.div>

      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12 pt-36 sm:pt-40 lg:pt-44 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-end">
          <motion.div style={{ y: textY }} className="lg:col-span-7 xl:col-span-8">
            <motion.div {...fade(0.1)} className="flex items-center gap-4">
              <span className="h-px w-10 bg-[#B8874A]" />
              <span className="eyebrow" data-testid="hero-eyebrow">Coaching &amp; Consulting</span>
            </motion.div>

            <h1 className="font-display mt-8 text-[2.9rem] leading-[1.04] sm:text-6xl lg:text-7xl xl:text-[5.6rem] tracking-[-0.02em]" data-testid="hero-headline">
              <MaskLine delay={0.25}>Built it.</MaskLine>
              <MaskLine delay={0.4}>Scaled it.</MaskLine>
              <MaskLine delay={0.55} className="italic text-[#B8874A]">Now I help you</MaskLine>
              <MaskLine delay={0.7} className="italic text-[#B8874A]">do the same.</MaskLine>
            </h1>

            <motion.p {...fade(1.0)} className="mt-9 max-w-xl text-base sm:text-lg leading-relaxed text-white/75" data-testid="hero-subhead">
              Operator-led consulting and coaching for founders, growth-stage businesses, and international beauty &amp; wellness brands entering India.
            </motion.p>

            <motion.div {...fade(1.15)} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <button
                onClick={() => scrollToId("contact")}
                className="btn-brass on-dark group inline-flex items-center justify-center gap-3 px-7 py-4 text-sm font-medium tracking-wide"
                data-testid="hero-book-discovery-button"
              >
                Book a Discovery Session
                <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
              <div data-testid="hero-founder">
                <p className="font-display text-xl">Sadiya</p>
                <p className="text-[13px] text-white/60 mt-0.5">Founder, Glam Studios — Scale Solutions</p>
              </div>
            </motion.div>

            <motion.ul {...fade(1.3)} className="mt-10 flex flex-wrap gap-2.5" data-testid="hero-tags">
              {HERO_TAGS.map((t) => (
                <li key={t} className="border border-white/20 px-4 py-1.5 text-[12px] tracking-wider uppercase text-white/80 hover:border-[#B8874A] hover:text-white transition-colors duration-300">
                  {t}
                </li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div style={{ y: cardY }} className="lg:col-span-5 xl:col-span-4">
            <Ledger />
          </motion.div>
        </div>

        <motion.button
          {...fade(1.6)}
          onClick={() => scrollToId("about")}
          className="hidden lg:flex mt-16 items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white transition-colors"
          data-testid="hero-scroll-indicator"
        >
          <ArrowDown className="h-4 w-4 animate-pulse" strokeWidth={1.25} /> Scroll
        </motion.button>
      </div>
    </section>
  );
};
